import { z } from 'zod'

/**
 * The admin's location policy (docs/specs/2026-10-26-location-v2-design.md "Admin policy", ruling R111a). Stored as the
 * provider_settings row `location` / `policy`; a missing field takes its default.
 */

export const LOCATION_LEVELS = ['country', 'region', 'area'] as const
export type LocationLevel = (typeof LOCATION_LEVELS)[number]

/** `required`: no automatic approval without a fix (a refusal is recorded, a person checks). `strict`: no way past S3. */
export const DEVICE_LOCATION_MODES = ['required', 'strict', 'optional'] as const
export type DeviceLocationMode = (typeof DEVICE_LOCATION_MODES)[number]

/** Why S3 has no fix (recorded by POST /registration/gps/refused). */
export const GPS_REFUSAL_REASONS = ['denied', 'unavailable', 'timeout', 'unsupported', 'skipped'] as const

export const VPN_POLICIES = ['review', 'ignore'] as const

export const LOCATION_RADIUS_KM = { min: 5, max: 200 } as const
export const LOCATION_MIN_ACCURACY_M = { min: 100, max: 20_000 } as const

/** Accuracy (km) a device fix needs to be conclusive at `region` and `country` (the spec's fixed 20 km and 50 km). */
export const LEVEL_ACCURACY_KM = { region: 20, country: 50 } as const

export const LocationPolicySchema = z.object({
  level: z.enum(LOCATION_LEVELS).default('area'),
  radiusKm: z.number().int().min(LOCATION_RADIUS_KM.min).max(LOCATION_RADIUS_KM.max).default(25),
  deviceLocation: z.enum(DEVICE_LOCATION_MODES).default('required'),
  minAccuracyM: z.number().int().min(LOCATION_MIN_ACCURACY_M.min).max(LOCATION_MIN_ACCURACY_M.max).default(3000),
  vpnPolicy: z.enum(VPN_POLICIES).default('review'),
  timezoneCheck: z.boolean().default(true),
})
export type LocationPolicy = z.infer<typeof LocationPolicySchema>

export const DEFAULT_LOCATION_POLICY: LocationPolicy = LocationPolicySchema.parse({})

/** A partial change from the admin page (merged over the stored policy, then validated). */
export const LocationPolicyPatchSchema = z.strictObject({
  level: z.enum(LOCATION_LEVELS).optional(),
  radiusKm: z.number().int().min(LOCATION_RADIUS_KM.min).max(LOCATION_RADIUS_KM.max).optional(),
  deviceLocation: z.enum(DEVICE_LOCATION_MODES).optional(),
  minAccuracyM: z.number().int().min(LOCATION_MIN_ACCURACY_M.min).max(LOCATION_MIN_ACCURACY_M.max).optional(),
  vpnPolicy: z.enum(VPN_POLICIES).optional(),
  timezoneCheck: z.boolean().optional(),
})
export type LocationPolicyPatch = z.infer<typeof LocationPolicyPatchSchema>

/** The accuracy (m) above which S3 shows the approximate-location hint: the level's conclusive accuracy. */
export function hintAccuracyM(p: Pick<LocationPolicy, 'level' | 'minAccuracyM'>): number {
  return p.level === 'area' ? p.minAccuracyM : LEVEL_ACCURACY_KM[p.level] * 1000
}
